import buildCanvasQueryKey from '@/hooks/canvas/helpers/buildCanvasQueryKey';
import invalidateCanvasGraph from '@/hooks/canvas/helpers/invalidateCanvasGraph';
import type { SerializedCanvasItem } from '@/hooks/canvas/types';
import applyCanvasLayoutChangesToItems from '../helpers/applyCanvasLayoutChangesToItems';
import coerceToRecord from '../helpers/coerceToRecord';
import eventMatchesToolNames from '../helpers/eventMatchesToolNames';
import getStringValue from '../helpers/getStringValue';
import parseCanvasLayoutChanges from '../helpers/parseCanvasLayoutChanges';
import type {
    BuildRefetchCanvasOnItemsLayoutUpdateSubscriptionParams,
    ChatEffectSubscription,
} from '../types';

const CANVAS_ITEMS_LAYOUT_UPDATE_TOOL_NAMES = [
    'update_canvas_items_layout',
    'canvases.updateItemsLayout',
];

const buildRefetchCanvasOnItemsLayoutUpdateSubscription = (
    _params: BuildRefetchCanvasOnItemsLayoutUpdateSubscriptionParams
): ChatEffectSubscription => {
    return {
        id: 'refetch-canvas-on-items-layout-update',
        routePattern: '/agents/:agentId/canvas/:canvasId',
        eventType: 'TOOL_CALL_RESULT',
        filter: ({ event, matchParams }) => {
            if (
                !eventMatchesToolNames({
                    event,
                    toolNames: CANVAS_ITEMS_LAYOUT_UPDATE_TOOL_NAMES,
                })
            ) {
                return false;
            }

            const args = coerceToRecord({ value: event.args });
            const canvasIdFromArgs = getStringValue({ value: args.canvasId });

            if (!canvasIdFromArgs) {
                return true;
            }

            return canvasIdFromArgs === matchParams.canvasId;
        },
        action: ({ event, queryClient, matchParams }) => {
            const args = coerceToRecord({ value: event.args });
            const changes = parseCanvasLayoutChanges({ value: args.changes });

            if (changes.length > 0) {
                queryClient.setQueryData(
                    buildCanvasQueryKey({ scope: 'items', canvasId: matchParams.canvasId }),
                    (existingItems: SerializedCanvasItem[] | undefined) =>
                        applyCanvasLayoutChangesToItems({
                            items: existingItems ?? [],
                            changes,
                        })
                );
            }

            invalidateCanvasGraph({
                queryClient,
                organizationId: matchParams.agentId,
                canvasId: matchParams.canvasId,
            });
        },
    };
};

export default buildRefetchCanvasOnItemsLayoutUpdateSubscription;
